import React, { useState, useEffect, useRef, useMemo } from "react";
import UploadImage from "./components/UploadImage";
import ColourPicker from "./components/ColourPicker";
import RecolourPreview from "./components/RecolourPreview";
import "./index.css";
import ColourExtractionSettings from "./components/ColourExtractionSettings";
import Logo from "./utils/logo.png";
import {
  hexToRgb,
  rgbToHex,
  getNearestColorName,
  hexToFloatRGB,
} from "./utils/colours";

import { extractPalette } from "./utils/api";

function App() {
  const isFirstUpload = useRef(true);
  const [imageFile, setImageFile] = useState(null);
  // 'colours' will hold user-selected replacement colours as RGB arrays.
  const [colours, setColours] = useState([]);
  // 'originalPalette' holds the full set of extracted dominant colours (as hex strings).
  const [originalPalette, setOriginalPalette] = useState([]);
  const [filteredPalette, setFilteredPalette] = useState(originalPalette || []);
  const [outputUrl, setOutputUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  // 'originalUrl' is an object URL for the uploaded image.
  const [originalUrl, setOriginalUrl] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const DEFAULT_COLOR_CONFIG = {
    num_colors: 10,
    oversampling: 3.0,
    merge_threshold: 41,
    min_pixel_thresh: 0.0,
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [colorConfig, setColorConfig] = useState(DEFAULT_COLOR_CONFIG);
  useEffect(() => {
    // Reset on new image
    setColorConfig(DEFAULT_COLOR_CONFIG);
  }, [imageFile]);

  const resetColorSettings = () => setColorConfig(DEFAULT_COLOR_CONFIG);

  // async function extractPalette(formData) {
  //   const res = await fetch("http://localhost:8000/extract_palette/", {
  //     method: "POST",
  //     body: formData,
  //   });
  //   if (!res.ok) throw new Error("Failed to extract palette");
  //   return await res.json(); // { palette: [...] }
  // }

  const handleExtractWithConfig = async () => {
    const formData = new FormData();
    formData.append("image", imageFile);

    Object.entries(colorConfig).forEach(([key, value]) =>
      formData.append(key, value)
    );

    try {
      const data = await extractPalette(formData);
      const extracted = data.palette.map((item) => item.color);

      setOriginalPalette(extracted);
      setFilteredPalette(extracted);
      setColours(data.palette.map((item) => hexToRgb(item.color)));
    } catch (err) {
      console.error("Palette extraction error:", err);
      alert("Failed to extract new palette");
    }
  };

  // Create an object URL for the original image whenever imageFile changes.
  useEffect(() => {
    if (imageFile) {
      const objectUrl = URL.createObjectURL(imageFile);
      setOriginalUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setOriginalUrl(null);
    }
  }, [imageFile]);

  // When a new image is uploaded, reset state and extract a palette of 10 dominant colors.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!imageFile) return;

    setOriginalPalette([]);
    setFilteredPalette([]);
    setOutputUrl(null);
    resetColorSettings();

    const formData = new FormData();
    formData.append("image", imageFile);

    extractPalette(formData)
      .then((data) => {
        const extracted = data.palette.map((item) => item.color);
        console.log("Extracted full palette:", extracted);

        setOriginalPalette(extracted);
        setFilteredPalette(extracted);
        if (isFirstUpload.current || colours.length === 0) {
          setColours(data.palette.map((item) => hexToRgb(item.color)));
          isFirstUpload.current = false;
        }
      })
      .catch((err) => console.error("Palette extraction error:", err));
  }, [imageFile]);

  // const originalColourNames = useMemo(() => {
  //   return filteredPalette.map((hex) => getNearestColorName(hex));
  // }, [filteredPalette]);

  // const replacementColourNames = useMemo(() => {
  //   return colours.map((rgb) => getNearestColorName(rgbToHex(rgb)));
  // }, [colours]);

  const handleSubmit = async () => {
    if (!imageFile || colours.length === 0) {
      return alert("Upload an image and select colours");
    }

    const formData = new FormData();
    formData.append("image", imageFile);
    // Ensure that the colours state (custom replacement palette) is actually being updated when the user changes it
    //console.log("Current custom palette (colours):", colours);
    // Convert user replacement palette (RGB arrays) into hex strings.
    formData.append(
      "colour_scheme",
      JSON.stringify(colours.map((rgb) => rgbToHex(rgb)))
    );
    // Also send the full extracted palette (originalPalette) as a JSON string.
    formData.append("extracted_palette", JSON.stringify(filteredPalette));

    formData.append("num_colors", colorConfig.num_colors);
    formData.append("oversampling", colorConfig.oversampling);
    formData.append("merge_threshold", colorConfig.merge_threshold);
    formData.append("min_pixel_thresh", colorConfig.min_pixel_thresh);

    setLoading(true);
    setOutputUrl(null);

    try {
      const res = await fetch("https://paletterix-backend.up.railway.app/recolour/", {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        console.log("Recolour response data:", data);

        const filename = data.output_image_url.split("/").pop();

        setOutputUrl(`https://paletterix-backend.up.railway.app/preview/${filename}`);
        setDownloadUrl(data.output_image_url);
        //setOutputUrl(imgUrl);
        setLoading(false);
      } else {
        setLoading(false);
        console.error("Recolour API returned error status:", res.status);
        alert("Failed to recolour");
      }
    } catch (err) {
      setLoading(false);
      console.error("Error submitting recolour:", err);
      alert("Failed to recolour");
    }
  };

  // --- Palette Export Helpers ---

  // Returns an export object with both palettes.
  const getExportPalette = () => {
    return {
      originalPalette: filteredPalette,
      customPalette: filteredPalette.map((orig, idx) =>
        colours[idx] ? rgbToHex(colours[idx]) : orig
      ),
    };
  };

  // Save palette as JSON.
  const savePaletteAsJSON = () => {
    const exportPalette = getExportPalette();
    const dataStr = JSON.stringify(exportPalette, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const urlBlob = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = urlBlob;
    a.download = "palette.json";
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(urlBlob);
    }, 1000);
  };

  // We'll use a dynamic array to accumulate bytes.
  let aseData = [];
  const writeBytes = (bytes) => {
    aseData.push(...bytes);
  };
  const writeUint16 = (value) => {
    writeBytes([(value >> 8) & 0xff, value & 0xff]);
  };
  const writeUint32 = (value) => {
    writeBytes([
      (value >> 24) & 0xff,
      (value >> 16) & 0xff,
      (value >> 8) & 0xff,
      value & 0xff,
    ]);
  };
  const writeFloat32 = (value) => {
    // Use a DataView to get the big-endian representation.
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);
    view.setFloat32(0, value, false); // false for big-endian
    writeBytes([
      view.getUint8(0),
      view.getUint8(1),
      view.getUint8(2),
      view.getUint8(3),
    ]);
  };
  const writeASEString = (str) => {
    writeUint16(str.length);
    for (let i = 0; i < str.length; i++) {
      writeUint16(str.charCodeAt(i));
    }
  };

  const writeColorEntry = (swatch) => {
    // swatch: { name, r, g, b }
    const name = swatch.name;
    // Calculate block content length: 2 + (name.length *2) + 4 (for "RGB ") + 12 (for 3 floats) + 2 (for color type)
    const blockContentLength = 2 + name.length * 2 + 4 + 12 + 2;
    writeUint16(0x0001); // color entry block type
    writeUint32(blockContentLength);
    writeASEString(name);
    // Write "RGB " as ASCII (4 bytes)
    writeBytes([0x52, 0x47, 0x42, 0x20]);
    writeFloat32(swatch.r);
    writeFloat32(swatch.g);
    writeFloat32(swatch.b);
    writeUint16(0x0000); // color type: global
  };

  const writeGroupStart = (groupName) => {
    // Group start block: type 0xC001, length = 2 + (groupName.length * 2)
    writeUint16(0xc001);
    writeUint32(2 + groupName.length * 2);
    writeASEString(groupName);
  };

  const writeGroupEnd = () => {
    // Group end block: type 0xC002, length = 0
    writeUint16(0xc002);
    writeUint32(0);
  };

  const createASEBuffer = (group1Name, swatches1, group2Name, swatches2) => {
    aseData = [];
    // Header: "ASEF"
    const headerStr = [0x41, 0x53, 0x45, 0x46];
    writeBytes(headerStr);
    writeUint16(1); // Version major (1)
    writeUint16(0); // Version minor (0)
    // Total blocks = 2 (group start/end) + swatches1.length + 2 (group start/end) + swatches2.length
    const totalBlocks = 2 + swatches1.length + 2 + swatches2.length;
    writeUint32(totalBlocks);
    // First group: Original Palette
    writeGroupStart(group1Name);
    swatches1.forEach((swatch) => {
      writeColorEntry(swatch);
    });
    writeGroupEnd();
    // Second group: Custom Palette
    writeGroupStart(group2Name);
    swatches2.forEach((swatch) => {
      writeColorEntry(swatch);
    });
    writeGroupEnd();
    return new Uint8Array(aseData).buffer;
  };

  const savePaletteAsASE = () => {
    const exportPalette = getExportPalette();
    // Convert hex codes into swatch objects with float RGB values.
    const originalSwatches = exportPalette.filteredPalette.map((hex) => {
      const { r, g, b } = hexToFloatRGB(hex);
      return { name: hex, r, g, b };
    });
    const customSwatches = exportPalette.customPalette.map((hex) => {
      const { r, g, b } = hexToFloatRGB(hex);
      return { name: hex, r, g, b };
    });
    const buffer = createASEBuffer(
      "Original Palette",
      originalSwatches,
      "Custom Palette",
      customSwatches
    );
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const urlBlob = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = urlBlob;
    a.download = "palette.ase";
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(urlBlob);
    }, 1000);
  };

  // --- Save Recoloured Image Helper ---
  // const handleSaveImage = async () => {
  //   if (!outputUrl) return;

  //   try {
  //     const response = await fetch(outputUrl);
  //     const blob = await response.blob();
  //     const objectUrl = URL.createObjectURL(blob);

  //     const a = document.createElement("a");
  //     a.href = objectUrl;
  //     a.download = "recoloured-image.png"; // Forces a file download
  //     document.body.appendChild(a);
  //     a.click();
  //     document.body.removeChild(a);

  //     // Clean up object URL
  //     URL.revokeObjectURL(objectUrl);
  //   } catch (error) {
  //     console.error("Error saving image:", error);
  //     alert("Failed to save the image.");
  //   }
  // };

  const resetPalette = () => {
    setFilteredPalette([...originalPalette]);
    // Do NOT reset `colours`; user edits are preserved.
    // Make sure the replacement colours match the new length
    setColours((prevColours) => {
      const updated = [...prevColours];
      const targetLength = originalPalette.length;

      while (updated.length < originalPalette.length) {
        updated.push(hexToRgb("#ffffff"));
      }

      return updated.slice(0, targetLength);
    });
  };

  // A separate memoized component for a single swatch.
  // const ReplacementSwatch = React.memo(
  //   ({ idx, orig, replacement, onColourChange, onRemove }) => (
  //     <div className="flex items-center gap-4 p-2 rounded border border-slate-200">
  //       {/* Original Color + Remove */}
  //       <div className="flex items-center gap-2 w-1/2">
  //         <div
  //           className="w-8 h-8 rounded-full border"
  //           style={{ backgroundColor: orig }}
  //           title={orig}
  //         />
  //         <span className="text-sm text-slate-700 font-medium">
  //           {/* Assume originalColourNames is computed at a higher level */}
  //           {originalColourNames[idx]}
  //         </span>
  //         <button
  //           onClick={() => onRemove(idx)}
  //           className="text-rose-500 hover:text-rose-700 text-xs"
  //           title="Remove colour"
  //         >
  //           ✕
  //         </button>
  //       </div>
  //       <span className="text-lg text-slate-500">→</span>
  //       <div className="flex items-center gap-2 w-1/2">
  //         <input
  //           type="color"
  //           value={replacement}
  //           onChange={(e) => onColourChange(idx, hexToRgb(e.target.value))}
  //           className="w-8 h-8 border rounded-full p-0"
  //           title={replacement}
  //         />
  //         <span className="text-sm text-slate-700 font-medium">
  //           {replacementColourNames[idx] || "—"}
  //         </span>
  //       </div>
  //     </div>
  //   )
  // );

  // --- End Palette Export Helpers ---

  return (
    <div className="min-h-screen bg-gradient-to-tr from-[#0a0a0a] via-[#1c1c1c] to-[#0a0a0a] text-neutral-100 font-serif p-8 items-center gap-8">
      <h1 className="text-4xl font-bold text-center tracking-wide text-[#f5f5f5] drop-shadow-[0_0_2px_#b89d5b] flex items-center justify-center gap-2">
        <img src={Logo} alt="Paletterix logo" className="w-8 h-8" />
        Paletterix
      </h1>

      {/* Step 1: Upload Image */}
      <div className="p-8 grid gap-6 place-items-center">
        <UploadImage onChange={setImageFile} />
      </div>
      {imageFile && (
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 items-start h-full">
          <div className="h-full flex flex-col justify-between">
            <div className="space-y-6 w-full max-w-4xl mx-auto">
              <div className="bg-[#121212] rounded-3xl border border-[#333] shadow-lg shadow-yellow-400/10 p-6 space-y-4">
                {/* Step 3: Preview */}

                {(outputUrl || loading || !!imageFile) && (
                  <div className="w-full max-w-5xl mx-auto px-4">
                    <div className="mb-6 border-b border-[#8e6e38] pb-2">
                      <h2 className="text-xl font-semibold tracking-wide text-[#e4ddc3] border-b border-[#8e6e38] uppercase">
                        Recolour Preview
                      </h2>

                      <RecolourPreview
                        originalUrl={originalUrl}
                        recolouredUrl={outputUrl}
                        loading={loading}
                        progressMessage={"Please wait for the image to load"}
                        timedOut={false}
                        onRetry={() => {}}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="h-full flex flex-col justify-between">
            {/* RIGHT: Mapping, Export, Submit */}
            <div className="space-y-6">
              {imageFile && originalPalette.length === 0 ? (
                <div className="bg-[#1a1a1a] rounded-2xl border border-[#2f2f2f] shadow-md shadow-[#b89d5b]/10 p-6 space-y-4 animate-pulse">
                  <h2 className="text-xl font-semibold tracking-wide text-[#e4ddc3] border-b border-[#8e6e38] pb-2 uppercase">
                    Replacement Mapping
                  </h2>
                  <div className="space-y-2">
                    {[...Array(10)].map((_, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-[#1a1a1a] border border-[#333] rounded-xl p-3 shadow-sm hover:shadow-md hover:border-[#b89d5b] transition"
                      >
                        <div className="flex items-center gap-2 w-1/2">
                          <div className="w-7 h-7 rounded-full bg-slate-200" />
                          <div className="h-4 bg-slate-200 rounded w-24" />
                        </div>
                        <span className="text-center text-lg text-slate-300">
                          →
                        </span>
                        <div className="flex items-center gap-2 w-1/2">
                          <div className="w-7 h-7 rounded-full bg-slate-200" />
                          <div className="h-4 bg-slate-200 rounded w-24" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                filteredPalette.length > 0 && (
                  <div className="w-full max-w-4xl mx-auto bg-[#181818] p-6 rounded-3xl border border-[#2a2a2a] shadow-md shadow-yellow-500/10 space-y-6 h-full flex flex-col overflow-y-auto">
                    <h2 className="text-xl font-semibold tracking-wide text-[#e4ddc3] border-b border-[#8e6e38] pb-2 uppercase">
                      Replacement Mapping
                    </h2>
                    <div className="relative">
                      <div className="absolute bottom-[0px] left-0 w-full h-6 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" />

                      <div className="overflow-y-auto grow space-y-4 max-h-[70vh] pb-20">
                        <ColourPicker
                          onChange={setColours}
                          originalPalette={originalPalette}
                          filteredPalette={filteredPalette}
                          setFilteredPalette={setFilteredPalette}
                          colours={colours}
                        />
                      </div>
                      <details className="mb-6 bg-[#1f1f1f] border border-[#3a3a3a] rounded-xl p-4 shadow-inner shadow-yellow-300/5">
                        <summary className="cursor-pointer text-sm font-semibold text-[#e4ddc3] hover:text-[#f2e9d8] transition">
                          Details (Only touch if the extracted palette is
                          missing some core colours)
                        </summary>

                        <div className="mt-4 space-y-4">
                          <ColourExtractionSettings
                            config={colorConfig}
                            onChange={setColorConfig}
                          />
                          <div className="mt-6 flex flex-wrap gap-3 justify-start">
                            <button
                              onClick={handleExtractWithConfig}
                              className="rounded-full px-5 py-2 text-sm font-medium bg-[#8e6e38] text-[#e4ddc3] hover:bg-[#a27c45] transition shadow"
                            >
                              Extract with These Settings
                            </button>

                            <button
                              onClick={resetColorSettings}
                              className="rounded-full px-5 py-2 text-sm font-medium border border-slate-600 text-slate-300 hover:border-white hover:text-white transition"
                            >
                              Reset to Default
                            </button>
                          </div>
                        </div>
                      </details>

                      <div className="sticky bottom-0 bg-[#1e1e1e] pt-4 pb-4 px-4 z-10 border-t border-[#444] shadow-inner">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {/* Reset */}
                          <button
                            onClick={resetPalette}
                            className="rounded-full px-4 py-2 text-sm font-medium bg-slate-700 text-[#e4ddc3] hover:bg-slate-600 transition-colors shadow-sm"
                          >
                            Reset Palette
                          </button>

                          {/* Save JSON */}
                          <button
                            onClick={savePaletteAsJSON}
                            className="rounded-full px-4 py-2 text-sm font-medium bg-[#5a4522] text-[#e4ddc3] hover:bg-[#6c5127] transition-colors shadow-sm"
                          >
                            Save as JSON
                          </button>

                          {/* Save ASE */}
                          <button
                            onClick={savePaletteAsASE}
                            className="rounded-full px-4 py-2 text-sm font-medium bg-[#5a4522] text-[#e4ddc3] hover:bg-[#6c5127] transition-colors shadow-sm"
                          >
                            Save as ASE
                          </button>

                          {/* Primary Action: Apply */}
                          <div className="col-span-2 md:col-span-3 flex justify-center pt-2 space-x-4">
                            <button
                              onClick={handleSubmit}
                              className="rounded-full bg-[#8e6e38] text-[#e4ddc3] font-semibold text-base px-6 py-2 shadow-md hover:bg-[#a27c45] transition"
                            >
                              Apply Recolour
                            </button>
                            {downloadUrl && (
                              <a
                                href={downloadUrl}
                                download
                                className="rounded-full bg-[#8e6e38] text-[#e4ddc3] font-semibold text-base px-6 py-2 shadow-md hover:bg-[#a27c45] transition"
                              >
                                Save Image
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
          <div className="mt-4 max-w-3xl space-y-6 text-sm text-slate-400 leading-relaxed">
            <h2 className="text-lg text-white font-bold">🎨 Tuning FAQs</h2>

            <div>
              <strong className="text-vedicGold">
                Q: How many colours are extracted by default?
              </strong>
              <p>
                The default is set to 10. If your image has a lot of variation
                (think: prints, gradients, noise), or just visibly more than 10
                dominant colours raise it. The algorithm clusters colours based
                on pixel dominance, so more colours = deeper scanning, slower
                render.
              </p>
            </div>

            <div>
              <strong className="text-vedicGold">
                Q: What does "Colour Variations" do?
              </strong>
              <p>
                In other terms, this is your oversampling. It's a backend
                power-up. Setting it to 3.0 means I’ll scan 3× more clusters
                than your requested number and then cherry-pick the most
                dominant ones. It helps surface hidden tones.
              </p>
            </div>

            <div>
              <strong className="text-vedicGold">
                Q: What’s a good Colour Similarity Threshold?
              </strong>
              <p>
                AKA merge threshold. This number controls how easily colours get
                grouped. It's based on RGB Euclidean distance. A value of
                <code>10</code> keeps colours very distinct; <code>60+</code>
                starts merging closer shades. Stick to <code>30–50</code> for
                balance.
              </p>
            </div>

            <div>
              <strong className="text-vedicGold">
                Q: What does "Min Pixel Threshold" mean?
              </strong>
              <p>
                It filters out irrelevant tiny clusters — like that one red
                pixel from a watermark. If your image is clean, keep this low (
                <code>0.00</code> to <code>0.01</code>). But if you're seeing
                random colours pop up, try <code>0.05</code> to
                <code>0.1</code>.
              </p>
            </div>

            <div>
              <strong className="text-vedicGold">
                Q: I tried everything, but the extraction is still off!
              </strong>
              <p>
                Try increasing the saturation of the image before uploading. The
                shades need to be distinct enough for the algorithm to work its
                magic.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
